import { RiInformationFill } from "@remixicon/react";
import React, {
    FormEvent, ReactElement, useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import {
    GetTicketsDocument, Permission, TicketInput, useCreateTicketMutation,
} from "../__generated__/api";
import Confirm from "../components/confirm/Confirm";
import Form, { FormCreateType, FormDisplayUserType } from "../components/form/Form";
import TimedMessage from "../components/timed-message/TimedMessage";
import usePortal from "./PortalContext";
import useRouting from "./Routing";
import { useTheme } from "./Theme";
import Drawer from "../components/theme/drawer/Drawer";
import { useTicketState } from "./ticket-state";

interface CreateTicketActions {
    show(requestTypeId?: string): void;
    onChangeRequestType(handler: ((id?: string) => void)): void;
    onDone(handler: ((id?: string) => void)): void;
}

export const CreateTicketContext = React.createContext<CreateTicketActions>({
    show() {
        // do nothing
    },
    onChangeRequestType() {
        // do nothing
    },
    onDone() {
        // do nothing
    },

});

export function CreateTicketProvider({ children }: {children: ReactElement}): (ReactElement | null) {
    const [isDisplayed, setIsDisplayed] = useState(false);
    const { permissions } = usePortal();
    const [ticketInput, setTicketInput] = useState<TicketInput>();
    const [createdId, setCreatedId] = useState<string | undefined>();
    const [requestTypeId, setRequestTypeId] = useState<string | undefined>();
    const { Paths, historyPush } = useRouting();

    const isTicketsPath = useMemo(() => window.location.pathname.startsWith(Paths.TICKETS), [window.location.pathname]);

    const [canCreate, setCanCreate] = useState(false);

    const ticketState = useTicketState();

    const [createTicket] = useCreateTicketMutation();
    const theme = useTheme();

    const hasPermissions = permissions.has(Permission.ChatsModify)
    || permissions.has(Permission.Administrator)
    || permissions.has(Permission.TicketCreateGuest)
    || permissions.has(Permission.TicketCreatePublic);

    const changeHandlers = useMemo<((id?: string) => void)[]>(() => [], []);
    const doneHandlers = useMemo<((id?: string) => void)[]>(() => [], []);

    const instance = useMemo(() => ({
        show: (typeId?: string) => {
            setIsDisplayed(true);
            if (typeId) {
                setRequestTypeId(typeId);
            }
        },
        onChangeRequestType(handler: ((id?: string) => void)): void {
            // eslint-disable-next-line react-hooks/rules-of-hooks
            useEffect(() => {
                changeHandlers.push(handler);
                return () => {
                    const index = changeHandlers.findIndex(item => item === handler);
                    if (index) {
                        changeHandlers.splice(index, 0);
                    }
                };
            // eslint-disable-next-line react-hooks/exhaustive-deps
            });
        },
        onDone(handler: ((id?: string) => void)): void {
            // eslint-disable-next-line react-hooks/rules-of-hooks
            useEffect(() => {
                doneHandlers.push(handler);
                return () => {
                    const index = doneHandlers.findIndex(item => item === handler);
                    if (index) {
                        doneHandlers.splice(index, 0);
                    }
                };
            // eslint-disable-next-line react-hooks/exhaustive-deps
            });
        },
    }), []);

    useEffect(() => {
        setTicketInput({
            ...ticketInput,
            customFields: [],
        });
    }, [ticketInput?.formId]);

    const setTicket = (ticket: Partial<TicketInput>) => {
        let customFields = ticketInput?.customFields;
        if (ticket.customField) {
            customFields = customFields ?? [];

            customFields.push(ticket.customField);
            customFields = customFields.dedupe(field => field.formFieldId, (__, last) => last);
        }
        const copy = {
            ...ticketInput,
            ...ticket,
            customFields,
        };
        delete copy.customField;

        setTicketInput(copy);
    };

    const onCreate = async (evt?: FormEvent) => {
        if (!ticketInput) {
            evt?.preventDefault();
            return;
        }
        setIsDisplayed(false);
        const response = await createTicket({
            refetchQueries: [
                isTicketsPath ? GetTicketsDocument : undefined,
            ].filterNull(),
            variables: {
                ticket: ticketInput,
            },
        });
        const ticketId = response.data?.saveTicket.ticketNumber?.toString();
        if (ticketId) {
            setCreatedId(ticketId);
            doneHandlers.forEach(handler => handler(ticketId));
        }
    };

    const formBody = isDisplayed ? (
        <Form
            variant="vertical"
            isEditable
            isEditing
            isFormField
            groupClassName={theme.isV3 ? "form-group" : ""}
            formId={requestTypeId}
            canSelectRequestType
            onChange={setTicket}
            onChangeRequestType={(_old, newType) => {
                changeHandlers.forEach(handler => handler(newType?.id));
            }}
            selectDefaultRequestType
            shouldShowDescriptions
            shouldApplyRequired
            displayUserType={FormDisplayUserType.REQUESTOR}
            setCreateType={type => setCanCreate(type === FormCreateType.CREATABLE)}
        />
    ) : undefined;

    return (
        <CreateTicketContext.Provider value={instance}>
            {createdId && (
                <TimedMessage
                    shouldShow
                    variant={theme.isV3 ? "success" : undefined}
                    actionText="View Request"
                    prefixIcon={<RiInformationFill />}
                    onActionClick={() => {
                        setCreatedId(undefined);
                        if (isTicketsPath) {
                            historyPush(Paths.TICKETS_VIEW, { ticketId: createdId });
                        } else {
                            historyPush(Paths.AGENT_CHAT, { chatId: createdId });
                        }
                    }}
                    onClose={() => setCreatedId(undefined)}
                >
                    Your ticket has been created
                </TimedMessage>
            )}
            {hasPermissions && (
                (theme.isV3 ? (
                    <Drawer
                        shouldShow={isDisplayed}
                        as="form"
                        cancelButtonText="Cancel"
                        title={canCreate ? "Create Ticket" : undefined}
                        saveButtonText={canCreate ? "Create" : undefined}
                        onSave={canCreate ? onCreate : undefined}
                        onHide={() => {
                            setIsDisplayed(false);
                            changeHandlers.forEach(handler => handler());
                        }}
                    >
                        {formBody}
                    </Drawer>
                ) : (
                    <Confirm
                        shouldShow={isDisplayed}
                        onHide={() => {
                            setIsDisplayed(false);
                            changeHandlers.forEach(handler => handler());
                        }}
                        closeText="Close"
                        confirmText={canCreate ? "Create" : undefined}
                        onConfirm={canCreate ? (evt => onCreate(evt)) : undefined}
                    >
                        {formBody}
                    </Confirm>
                ))

            )}
            {children}
        </CreateTicketContext.Provider>
    );
}

export default function useCreateTicket(): CreateTicketActions {
    return useContext(CreateTicketContext) ?? {};
}
